<template>
  <div
    ref="chart"
    class="ct-chart"
    :style="{ height: this.height + 'px' }"
  ></div>
</template>

<script>
  import Chartist from 'chartist';
  import 'chartist/dist/chartist.min.css';
  import ChartistTooltip from 'chartist-plugin-tooltips-updated';
  import domtoimage from 'dom-to-image';
  import { isEmpty } from 'lodash/fp';
  import { du } from '@/ui/i18n/i18n';
  import ChartistLegend from 'chartist-plugin-legend';

  const DAY = 60 * 60 * 24;

  export default {
    props: {
      height: {
        type: Number,
        default: 200,
      },
      chartData: {
        type: Object,
        default: null,
      },
      chartStartsAt: {
        type: Number,
        required: true,
      },
      chartEndsAt: {
        type: Number,
        required: true,
      },
      showPoint: {
        type: Boolean,
        default: true,
      },
      onlyInteger: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: 'line',
      },
      tooltipText: {
        type: String,
        required: true,
      },
      high: {
        type: Number,
      },
      low: {
        type: Number
      },
      formatYLabels: {
        type: [Function, undefined],
        default: undefined,
      },
      chartPadding: {
        type: Object,
        default: () => ({
          top: 40,
          right: 60,
          bottom: 20,
          left: 10,
        }),
      },
      hasLegends: {
        type: Boolean,
        default: false,
      },
      legendId: {
        type: String,
      },
      showArea: {
        type: Boolean,
        default: true,
      },
      interpolationSimple: {
        type: Boolean,
        default: false,
      },
      axisXFixed: {
        type: Boolean,
        default: true,
      },
      axisXShowGrid: {
        type: Boolean,
        default: true,
      },
      axisYOffset: {
        type: Number,
        default: 50,
      },
      supportsForeignObject: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      chartist: null,
      chartTicks: [],
      chartTickPruneFactor: 1,
    }),

    computed: {
      ChartConstructor() {
        switch (this.type) {
          case 'line':
            return Chartist.Line;
          case 'bar':
            return Chartist.Bar;
          default:
            return Chartist.Line;
        }
      },

    },

    watch: {
      chartData() {
        if (this.chartist) {
          this.destroyChart();
        }

        if (this.chartData) {
          this.drawChart();
        }
      },
      type() {
        if (this.chartist) {
          this.destroyChart();
        }

        if (this.chartData) {
          this.drawChart();
        }
      },
    },

    mounted() {
      if (!isEmpty(this.chartData)) {
        this.drawChart();
      }
    },

    destroyed() {
      this.destroyChart();
    },

    methods: {

      destroyChart() {
        this.chartTickPruneFactor = 1;
        this.chartDisplayHours = false;
        this.chartTicks = [];

        if (this.chartist) {
          this.chartist.detach();
        }

        this.chartist = null;
      },

      drawChart() {
        this.chartist = new this.ChartConstructor(
          this.$refs.chart,
          this.chartData,
          {
            lineSmooth: this.interpolationSimple ? Chartist.Interpolation.simple() : Chartist.Interpolation.none(),
            high: this.high,
            low: this.low,
            fullWidth: true,
            showPoint: this.showPoint,
            showLine: true,
            showArea: this.showArea,
            chartPadding: this.chartPadding,
            axisX: this.getAxisX(),
            axisY: {
              offset: this.axisYOffset,
              labelInterpolationFnc: this.formatValue,
              onlyInteger: this.onlyInteger,
            },
            plugins: [
              ...(this.hasLegends ? [ChartistLegend({
                clickable: false,
                position: document.getElementById(this.legendId),
              })] : []),
              ChartistTooltip({
                anchorToPoint: true,
                tooltipFnc: (meta, value) => {
                  const decodedMeta = Chartist.deserialize(meta);
                  const decodedValue = value.split(',')[1];
                  // (don't forget leap seconds)
                  const rangeIsOneDay = (decodedMeta.end - decodedMeta.start) <= (DAY + 1);
                  const dateLine = rangeIsOneDay
                    ? du(decodedMeta.start, 'shortDate')
                    : `${du(decodedMeta.start, 'shortDate')} - ${du(decodedMeta.end, 'shortDate')}`;
                  return `<div class="mb-2 text-60">
                            ${dateLine}
                          </div>
                          <span class="text-2xl font-bold">
                            ${this.$n(decodedValue, this.chartData.format)}
                          </span> ${this.tooltipText}`;
                },
              }),
            ],
          },
        );
        // this value as false is needed to make png export possible
        this.chartist.supportsForeignObject = this.supportsForeignObject;
      },

      getAxisX() {
        const MAX_TICKS = 20;
        const pruneFactor = parseInt(Math.ceil(this.chartData.labels.length / MAX_TICKS), 10);

        return this.axisXFixed
          ? {
            showGrid: this.axisXShowGrid,
            type: Chartist.FixedScaleAxis,
            ticks: this.chartData.labels.reduce((result, _, index) => {
              if (index % pruneFactor === 0) {
                result.push(index);
              }
              return result;
            }, []),
            labelInterpolationFnc: value => this.formatLabel(this.chartData.labels[value]),
          } : {
            showGrid: this.axisXShowGrid,
            labelInterpolationFnc: value => this.formatLabel(value),
          };
      },

      formatValue(value) {
        if (this.formatYLabels) {
          return this.formatYLabels(value);
        }

        if (this.chartData.format) {
          return this.$n(value, this.chartData.format);
        }

        return value;
      },

      formatLabel(label) {
        let format = 'shortDateOfYear';
        if (this.chartDisplayHours) {
          format = 'time';
        }
        return du(label, format);
      },

      downloadPng() {
        domtoimage.toPng(this.$refs.chart)
          .then(dataUrl => {
            const img = new Image();
            img.src = dataUrl;
            const link = document.createElement('a');
            link.download = 'chart.png';
            link.href = dataUrl;
            link.click();
          });
      },
    },
  };
</script>
